/**
 * SearchBar — search input with photo icon inside + find button in toolbar
 */

import { createElement } from '../utils/dom.js';
import { EVENTS } from '../core/events.js';

// Classic camera SVG icon
const CAMERA_ICON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>';

// Magnifying glass SVG icon
const SEARCH_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>';

export default class SearchBar {
  /**
   * @param {Object} config
   * @param {Object} eventBus
   * @param {Object} i18n
   */
  constructor(config, eventBus, i18n) {
    this._config = config;
    this._eventBus = eventBus;
    this._i18n = i18n;

    this._el = null;
    this._inputWrapEl = null;
    this._inputEl = null;
    this._spinnerEl = null;
    this._photoBtn = null;
  }

  /**
   * Render the search bar into the toolbar
   * @param {HTMLElement} container - Toolbar element
   */
  render(container) {
    this._el = createElement('div', 'grb-widget--search-bar');

    // Input wrapper (input + photo icon inside)
    this._inputWrapEl = createElement('div', 'grb-widget--search-input-wrap');

    this._inputEl = createElement('input', 'grb-widget--search-input', {
      type: 'text',
      placeholder: this._i18n.t('search.placeholder'),
      autocomplete: 'off',
    });

    // Photo button inside input (right side)
    this._photoBtn = createElement('button', 'grb-widget--photo-btn', {
      type: 'button',
      'aria-label': this._i18n.t('search.photo'),
    });
    this._photoBtn.innerHTML = CAMERA_ICON;

    // Loading spinner (same position as photo btn)
    this._spinnerEl = createElement('div', 'grb-widget--search-spinner');

    // Search icon (left side of input)
    const searchIcon = createElement('span', 'grb-widget--search-icon');
    searchIcon.innerHTML = SEARCH_ICON;

    this._inputWrapEl.appendChild(searchIcon);
    this._inputWrapEl.appendChild(this._inputEl);
    this._inputWrapEl.appendChild(this._photoBtn);
    this._inputWrapEl.appendChild(this._spinnerEl);

    // Search button (accent style)
    const searchBtn = createElement('button', 'grb-widget--search-btn', {
      type: 'button',
    }, [this._i18n.t('search.button')]);

    this._el.appendChild(this._inputWrapEl);
    this._el.appendChild(searchBtn);

    container.appendChild(this._el);

    // Event handlers
    this._inputEl.addEventListener('input', () => {
      this._eventBus.emit('input:change', { value: this._inputEl.value });
    });

    this._inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const query = this._inputEl.value.trim();
        if (query) {
          this._eventBus.emit(EVENTS.SEARCH_START, { query });
        }
      }
    });

    searchBtn.addEventListener('click', () => {
      const query = this._inputEl.value.trim();
      if (query) {
        this._eventBus.emit(EVENTS.SEARCH_START, { query });
      }
    });

    this._photoBtn.addEventListener('click', () => {
      this._eventBus.emit('photo:trigger');
    });
  }

  getValue() {
    return this._inputEl ? this._inputEl.value : '';
  }

  setValue(value) {
    if (this._inputEl) {
      this._inputEl.value = value;
    }
  }

  focus() {
    if (this._inputEl) {
      this._inputEl.focus();
    }
  }

  setLoading(loading) {
    if (this._spinnerEl) {
      this._spinnerEl.classList.toggle('grb-widget--active', loading);
    }
    if (this._photoBtn) {
      this._photoBtn.style.display = loading ? 'none' : '';
    }
  }

  getEl() {
    return this._el;
  }

  getInputWrapEl() {
    return this._inputWrapEl;
  }

  destroy() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }
    this._el = null;
    this._inputEl = null;
  }
}
